import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getEstimatesByJob, getLabourEstimatesByJob } from '@/lib/api/estimates';
import type { Estimate, LabourEstimate } from '@/lib/api/estimates';
import { createQuoteFromEstimate } from '@/lib/api/quotes';
import { estimatePath, labourEstimatePath, quotePath } from '@/lib/utils/navPaths';
import { EstimateBuilderModal } from './EstimateBuilderModal';
import { LabourEstimateModal } from './LabourEstimateModal';

interface MaterialEstimatesTabProps {
  jobId: string;
}

export const MaterialEstimatesTab = ({ jobId }: MaterialEstimatesTabProps) => {
  const navigate = useNavigate();
  const { clientId } = useParams<{ clientId?: string }>();
  const [estimates, setEstimates] = useState<Estimate[]>([]);
  const [labourEstimates, setLabourEstimates] = useState<LabourEstimate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [activeEstimateId, setActiveEstimateId] = useState<number | null>(null);
  const [showLabourModal, setShowLabourModal] = useState(false);
  const [activeLabourId, setActiveLabourId] = useState<number | null>(null);
  const [converting, setConverting] = useState(false);

  // Estimate → quote → invoice workflow (#96): bundle every un-quoted estimate
  // on this job into one client-ready quote and open it in the builder.
  const convertAllToQuote = async () => {
    const mat = estimates.filter(e => !e.quote_id).map(e => e.id);
    const lab = labourEstimates.filter(e => !e.quote_id).map(e => e.id);
    if (!mat.length && !lab.length) return;
    setConverting(true);
    try {
      const r = await createQuoteFromEstimate(mat, lab, Number(jobId));
      navigate(quotePath(clientId, jobId, r.id));
    } catch {
      setError('Could not create the quote — try again.');
    } finally {
      setConverting(false);
    }
  };

  const openCreate = () => { setActiveEstimateId(null); setShowModal(true); };
  // Editing goes through the detail pages now (one canonical editor, #96);
  // the modals are kept for CREATE only — the builder modal silently stripped
  // section headings from existing estimates on save.
  const openLabourCreate = () => { setActiveLabourId(null); setShowLabourModal(true); };

  useEffect(() => {
    loadEstimates();
  }, [jobId]);

  const loadEstimates = async () => {
    try {
      setLoading(true);
      setError(null);
      const [materialData, labourData] = await Promise.all([
        getEstimatesByJob(jobId),
        getLabourEstimatesByJob(jobId),
      ]);
      setEstimates(materialData);
      setLabourEstimates(labourData);
    } catch (error) {
      console.error('Failed to load estimates:', error);
      setError('Failed to load estimates. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-CA', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'approved':
        return 'bg-green-100 text-green-700';
      case 'sent':
        return 'bg-blue-100 text-blue-700';
      case 'draft':
        return 'bg-gray-50 text-gray-700';
      default:
        return 'bg-gray-50 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
          <p className="text-gray-600">Loading estimates...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <span className="material-symbols-outlined text-6xl text-red-500 mb-4">error</span>
          <p className="text-gray-900 font-semibold mb-2">Failed to load estimates</p>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={loadEstimates}
            className="px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // DECIMAL columns arrive as strings — Number() or these become string
  // concatenation and the header renders NaN (#96)
  const materialTotal = estimates.reduce((sum, e) => sum + (Number(e.total_amount) || 0), 0);
  const labourTotal = labourEstimates.reduce((sum, e) => sum + (Number(e.total_amount) || 0), 0);
  const combinedTotal = materialTotal + labourTotal;
  const totalCount = estimates.length + labourEstimates.length;
  const unquotedCount = estimates.filter(e => !e.quote_id).length + labourEstimates.filter(e => !e.quote_id).length;

  return (
    <div>
      <div className="flex justify-between items-center mb-6 gap-3 flex-wrap">
        <div>
          <h2 className="text-gray-900 text-[22px] font-bold leading-tight tracking-[-0.015em]">
            Material & Labour Estimates
          </h2>
          <p className="text-gray-600 text-sm mt-1">
            {totalCount} {totalCount === 1 ? 'estimate' : 'estimates'} • Combined Total: {formatCurrency(combinedTotal)}
          </p>
        </div>
        {unquotedCount > 0 && (
          <button
            onClick={convertAllToQuote}
            disabled={converting}
            title="Bundle the un-quoted estimates into one quote — the workflow is estimate → quote → invoice"
            className="flex items-center gap-1.5 px-3 py-2 bg-white border border-primary/40 text-primary rounded-lg text-sm font-semibold hover:bg-primary/5 disabled:opacity-50"
          >
            <span className={`material-symbols-outlined text-base ${converting ? 'animate-spin' : ''}`}>{converting ? 'progress_activity' : 'request_quote'}</span>
            {converting ? 'Converting…' : `Convert to quote (${unquotedCount})`}
          </button>
        )}
      </div>

      {/* Combined Summary */}
      {totalCount > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              <span className="material-symbols-outlined text-gray-600 text-sm">inventory_2</span>
              <p className="text-gray-600 text-sm">Materials</p>
            </div>
            <p className="text-2xl font-bold text-gray-900">{formatCurrency(materialTotal)}</p>
            <p className="text-gray-500 text-xs mt-1">{estimates.length} {estimates.length === 1 ? 'estimate' : 'estimates'}</p>
          </div>

          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              <span className="material-symbols-outlined text-gray-600 text-sm">engineering</span>
              <p className="text-gray-600 text-sm">Labour</p>
            </div>
            <p className="text-2xl font-bold text-gray-900">{formatCurrency(labourTotal)}</p>
            <p className="text-gray-500 text-xs mt-1">{labourEstimates.length} {labourEstimates.length === 1 ? 'estimate' : 'estimates'}</p>
          </div>

          <div className="bg-primary/5 rounded-lg p-4 border border-primary/20">
            <div className="flex items-center gap-2 mb-2">
              <span className="material-symbols-outlined text-primary text-sm">calculate</span>
              <p className="text-gray-600 text-sm">Combined Total</p>
            </div>
            <p className="text-2xl font-bold text-gray-900">{formatCurrency(combinedTotal)}</p>
          </div>
        </div>
      )}

      {/* Material Estimates Section */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-gray-900 font-semibold text-base flex items-center gap-2">
            <span className="material-symbols-outlined text-gray-600 text-lg">inventory_2</span>
            Material Estimates
          </h3>
          <button
            onClick={openCreate}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-primary hover:bg-primary/90 text-white rounded-lg text-sm font-medium transition-colors"
          >
            <span className="material-symbols-outlined text-base">add</span>
            New Estimate
          </button>
        </div>

        {estimates.length === 0 ? (
          <div className="text-center py-8 bg-gray-50 rounded-xl">
            <span className="material-symbols-outlined text-4xl text-gray-400 mb-3">
              inventory_2
            </span>
            <p className="text-gray-500 text-sm">No material estimates for this job</p>
          </div>
        ) : (
          <div className="grid gap-3">
            {estimates.map((estimate) => (
              <div
                key={estimate.id}
                className="bg-white rounded-xl p-4 border border-gray-200 hover:border-primary transition-colors"
              >
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="text-gray-900 font-bold">{estimate.title}</h4>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(estimate.status)}`}>
                        {estimate.status}
                      </span>
                    </div>
                    <p className="text-gray-500 text-xs">
                      Created {formatDate(estimate.created_at)}
                    </p>
                  </div>
                  <span className="text-gray-900 font-bold text-lg">
                    {formatCurrency(Number(estimate.total_amount) || 0)}
                  </span>
                </div>

                <div className="flex gap-2 mt-3 items-center">
                  <button
                    onClick={() => navigate(estimatePath(clientId, jobId, estimate.id))}
                    className="flex items-center gap-1 px-3 py-1.5 bg-primary hover:bg-primary/90 text-white rounded-lg transition-colors text-sm"
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>edit</span>
                    Open
                  </button>
                  {estimate.quote_id && (
                    <button
                      onClick={() => navigate(quotePath(clientId, jobId, estimate.quote_id!))}
                      className="flex items-center gap-1 px-2.5 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold hover:bg-green-200"
                      title="Converted — open the quote"
                    >
                      <span className="material-symbols-outlined text-sm">request_quote</span>
                      {estimate.quote_number || 'Quoted'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Labour Estimates Section */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-gray-900 font-semibold text-base flex items-center gap-2">
            <span className="material-symbols-outlined text-gray-600 text-lg">engineering</span>
            Labour Estimates
          </h3>
          <button
            onClick={openLabourCreate}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-primary hover:bg-primary/90 text-white rounded-lg text-sm font-medium transition-colors"
          >
            <span className="material-symbols-outlined text-base">add</span>
            New Estimate
          </button>
        </div>

        {labourEstimates.length === 0 ? (
          <div className="text-center py-8 bg-gray-50 rounded-xl">
            <span className="material-symbols-outlined text-4xl text-gray-400 mb-3">
              engineering
            </span>
            <p className="text-gray-500 text-sm">No labour estimates for this job</p>
          </div>
        ) : (
          <div className="grid gap-3">
            {labourEstimates.map((estimate) => (
              <div
                key={estimate.id}
                className="bg-white rounded-xl p-4 border border-gray-200 hover:border-primary transition-colors"
              >
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="text-gray-900 font-bold">{estimate.title}</h4>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(estimate.status)}`}>
                        {estimate.status}
                      </span>
                    </div>
                    <p className="text-gray-500 text-xs">
                      {Number(estimate.total_hours)}h @ {formatCurrency(Number(estimate.hourly_rate) || 0)}/hr • Created {formatDate(estimate.created_at)}
                    </p>
                  </div>
                  <span className="text-gray-900 font-bold text-lg">
                    {formatCurrency(Number(estimate.total_amount) || 0)}
                  </span>
                </div>

                <div className="flex gap-2 mt-3 items-center">
                  <button
                    onClick={() => navigate(labourEstimatePath(clientId, jobId, estimate.id))}
                    className="flex items-center gap-1 px-3 py-1.5 bg-primary hover:bg-primary/90 text-white rounded-lg transition-colors text-sm"
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>edit</span>
                    Open
                  </button>
                  {estimate.quote_id && (
                    <button
                      onClick={() => navigate(quotePath(clientId, jobId, estimate.quote_id!))}
                      className="flex items-center gap-1 px-2.5 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold hover:bg-green-200"
                      title="Converted — open the quote"
                    >
                      <span className="material-symbols-outlined text-sm">request_quote</span>
                      {estimate.quote_number || 'Quoted'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showModal && (
        <EstimateBuilderModal
          jobId={jobId}
          estimateId={activeEstimateId}
          onClose={() => setShowModal(false)}
          onSaved={() => { loadEstimates(); setShowModal(false); }}
        />
      )}

      {showLabourModal && (
        <LabourEstimateModal
          jobId={jobId}
          estimateId={activeLabourId}
          onClose={() => setShowLabourModal(false)}
          onSaved={() => { loadEstimates(); setShowLabourModal(false); }}
        />
      )}
    </div>
  );
};
